import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import { useStyletron } from 'baseui';
import { HeadingLarge, HeadingMedium, HeadingSmall, ParagraphMedium } from 'baseui/typography';
import { Button } from 'baseui/button';
import { Card, StyledBody } from 'baseui/card';
import { ArrowLeft, Menu } from 'baseui/icon';
import { Drawer, ANCHOR } from 'baseui/drawer';
import { Accordion, Panel } from 'baseui/accordion';

import {
  PageContainer,
  Sidebar,
  MainContent,
  SectionContainer,
  FloatingMenuButton,
  NavItem,
  ScreenshotImage,
  AccordionWrapper,
  accordionOverrides
} from '../styles/manualStyles';

import { renderTextWithIcons } from '../utils/renderManualText';

const ManualPage = () => {
  const { t, locale } = useLocale();
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [css] = useStyletron();

  const sections = useMemo(() => [
    { id: 'intro', title: t('pages.manual.introTitle') },
    { id: 'signup', title: t('pages.manual.signupTitle') },
    { id: 'dashboard', title: t('pages.manual.dashboardTitle') },
    { id: 'controller', title: t('pages.manual.controllerTitle') },
    { id: 'info', title: t('pages.manual.displayInfoTitle') },
    { id: 'settings', title: t('pages.manual.settingsTitle') },
    { id: 'share', title: t('pages.manual.shareTitle') },
    { id: 'display', title: t('pages.manual.displayModeTitle') },
  ], [t]);

  const handleBack = () => {
    navigate(-1);
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsDrawerOpen(false);
  };

  const NavContent = () => (
    <>
      <HeadingMedium margin="0 0 16px 0">{t('pages.manual.title')}</HeadingMedium>
      {sections.map((section) => (
        <NavItem key={section.id} onClick={() => scrollToSection(section.id)}>
          {section.title}
        </NavItem>
      ))}
      <Button
        onClick={() => navigate(-1)}
        kind="secondary"
        size="compact"
        style={{ marginTop: '24px', width: '100%' }}
      >
        {t('common.back')}
      </Button>
    </>
  );

  return (
    <PageContainer>
      <Sidebar>
        <NavContent />
      </Sidebar>

      <MainContent>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '32px', gap: '16px' }}>
          <Button onClick={handleBack} kind="secondary" shape="circle" size="compact">
            <ArrowLeft size={24} />
          </Button>
          <HeadingLarge margin="0">{t('pages.manual.title')}</HeadingLarge>
        </div>

        <SectionContainer id="intro">
          <Card title={t('pages.manual.introTitle')}>
            <StyledBody>
              <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>
                {t('pages.manual.introContent')}
              </ParagraphMedium>
            </StyledBody>
          </Card>
        </SectionContainer>

        <SectionContainer id="signup">
          <HeadingMedium>{t('pages.manual.gettingStartedTitle')}</HeadingMedium>
          <ParagraphMedium>{t('pages.manual.gettingStartedContent')}</ParagraphMedium>
          <ScreenshotImage src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/login.png`} alt="Login Screen" />

          <HeadingSmall margin="24px 0 8px 0">{t('pages.manual.signupTitle')}</HeadingSmall>
          <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{t('pages.manual.signupContent')}</ParagraphMedium>
          <ScreenshotImage src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/signup.png`} alt="Sign Up" />
        </SectionContainer>

        <SectionContainer id="dashboard">
          <HeadingMedium>{t('pages.manual.dashboardTitle')}</HeadingMedium>
          <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.dashboardIntro'))}</ParagraphMedium>
          <ScreenshotImage src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/dashboard.png`} alt="Dashboard" />

          <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.dashboardSettings'))}</ParagraphMedium>
          <ScreenshotImage $width="320px" src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/settings_modal.png`} alt="Settings Modal" />

          <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.dashboardOther'))}</ParagraphMedium>
        </SectionContainer>

        <SectionContainer id="controller">
          <HeadingMedium>{t('pages.manual.controllerTitle')}</HeadingMedium>
          <ScreenshotImage src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/controller.png`} alt="Controller Interface" />

          <HeadingSmall>{t('pages.manual.controllerTimerTitle')}</HeadingSmall>
          <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.controllerTimerContent'))}</ParagraphMedium>

          <HeadingSmall>{t('pages.manual.controllerStatsTitle')}</HeadingSmall>
          <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.controllerStatsContent'))}</ParagraphMedium>

          <HeadingSmall>{t('pages.manual.controllerStructureTitle')}</HeadingSmall>
          <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.controllerStructureIntro'))}</ParagraphMedium>

          <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.controllerStructureRows'))}</ParagraphMedium>
          <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.controllerStructureColumns'))}</ParagraphMedium>
          <ScreenshotImage src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/structure_mixed_game.png`} alt="Mixed Game Structure" />

          <AccordionWrapper>
            <Accordion overrides={accordionOverrides}>
              <Panel title={t('pages.manual.controllerStructureBreakMessagesIntro').split('\n')[0]}>
                <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>
                  {renderTextWithIcons(t('pages.manual.controllerStructureBreakMessagesIntro').split('\n').slice(1).join('\n'))}
                </ParagraphMedium>
                <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.controllerStructureBreakMessagesStep1'))}</ParagraphMedium>
                <ScreenshotImage src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/break_menu_message.png`} alt="Break Menu Message" />
                <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.controllerStructureBreakMessagesStep2'))}</ParagraphMedium>
                <ScreenshotImage $width="320px" src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/break_message_modal.png`} alt="Break Message Modal" />
                <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.controllerStructureBreakMessagesStep3'))}</ParagraphMedium>
                <ScreenshotImage $width="240px" src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/break_message_icon.png`} alt="Break Message Icon" />
              </Panel>
              <Panel title={t('pages.manual.controllerStructurePreTournamentIntro').split('\n')[0]}>
                <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>
                  {renderTextWithIcons(t('pages.manual.controllerStructurePreTournamentIntro').split('\n').slice(1).join('\n'))}
                </ParagraphMedium>
                <ScreenshotImage src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/pre_tournament_break.png`} alt="Pre-tournament Break" />
                <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.controllerStructurePreTournamentScheduled'))}</ParagraphMedium>
                <ScreenshotImage $width="320px" src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/schedule_modal.png`} alt="Schedule Modal" />
                <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.controllerStructurePreTournamentManual'))}</ParagraphMedium>
              </Panel>
            </Accordion>
          </AccordionWrapper>

          <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.controllerStructureApply'))}</ParagraphMedium>

          <HeadingSmall>{t('pages.manual.controllerToolbarTitle')}</HeadingSmall>
          <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.controllerToolbarContent'))}</ParagraphMedium>
        </SectionContainer>

        <SectionContainer id="info">
          <HeadingMedium>{t('pages.manual.displayInfoTitle')}</HeadingMedium>
          <ParagraphMedium>{t('pages.manual.displayInfoContent')}</ParagraphMedium>
          <ScreenshotImage $width="320px" src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/info_modal.png`} alt="Info Modal" />
        </SectionContainer>

        <SectionContainer id="settings">
          <HeadingMedium>{t('pages.manual.settingsTitle')}</HeadingMedium>
          <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.settingsIntro'))}</ParagraphMedium>
          <ScreenshotImage $width="320px" style={{ marginBottom: '24px' }} src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/controller_settings_modal.png`} alt="Settings Modal" />

          <Accordion overrides={accordionOverrides}>
            <Panel title={t('pages.manual.settingsIntegratedTitle')}>
              <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.settingsIntegratedPart1'))}</ParagraphMedium>
              <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.settingsIntegratedPart2'))}</ParagraphMedium>
              <ScreenshotImage $width="320px" src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/integrated_view_portrait.png`} alt="Integrated View Portrait" />
              <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.settingsIntegratedPart3'))}</ParagraphMedium>
              <ScreenshotImage src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/integrated_view_landscape.png`} alt="Integrated View Landscape" />
              <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.settingsIntegratedPart4'))}</ParagraphMedium>
            </Panel>
            <Panel title={t('pages.manual.settingsProgressTitle')}>
              <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.settingsProgressContent'))}</ParagraphMedium>
            </Panel>
          </Accordion>
        </SectionContainer>

        <SectionContainer id="share">
          <HeadingMedium>{t('pages.manual.shareTitle')}</HeadingMedium>
          <ParagraphMedium>{t('pages.manual.shareContent')}</ParagraphMedium>
          <ScreenshotImage $width="320px" src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/share_modal.png`} alt="Share Modal" />
        </SectionContainer>

        <SectionContainer id="display">
          <HeadingMedium>{t('pages.manual.displayModeTitle')}</HeadingMedium>
          <ParagraphMedium>{t('pages.manual.displayModeContent')}</ParagraphMedium>
          <ScreenshotImage src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/display.png`} alt="Display Mode" />

          <HeadingSmall margin="24px 0 8px 0">{t('pages.manual.displayAreasTitle')}</HeadingSmall>
          <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.displayAreasContent'))}</ParagraphMedium>

          <HeadingSmall margin="24px 0 8px 0">{t('pages.manual.displayOptionsTitle')}</HeadingSmall>
          <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.displayOptionsContent'))}</ParagraphMedium>


          <ParagraphMedium style={{ whiteSpace: 'pre-line', marginTop: '24px' }} />
          <AccordionWrapper>
            <Accordion overrides={accordionOverrides}>
              <Panel title={t('pages.manual.displaySettingsTitle')}>
                <ParagraphMedium style={{ whiteSpace: 'pre-line' }}>{renderTextWithIcons(t('pages.manual.displaySettingsIntro'))}</ParagraphMedium>

                <ParagraphMedium style={{ whiteSpace: 'pre-line', marginTop: '16px' }}>{renderTextWithIcons(t('pages.manual.displaySettingsTheme'))}</ParagraphMedium>
                <ScreenshotImage $width="320px" src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/display_settings_theme.png`} alt="Display Settings - Theme" />

                <ParagraphMedium style={{ whiteSpace: 'pre-line', marginTop: '16px' }}>{renderTextWithIcons(t('pages.manual.displaySettingsTts'))}</ParagraphMedium>
                <ScreenshotImage $width="320px" src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/display_settings_tts.png`} alt="Display Settings - TTS" />

                <ParagraphMedium style={{ whiteSpace: 'pre-line', marginTop: '16px' }}>{renderTextWithIcons(t('pages.manual.displaySettingsSound'))}</ParagraphMedium>
                <ScreenshotImage $width="320px" src={`/images/manual/${locale === 'ja' ? 'ja' : 'en'}/display_settings_sound.png`} alt="Display Settings - Sound" />
              </Panel>
            </Accordion>
          </AccordionWrapper>
        </SectionContainer>

      </MainContent>

      <FloatingMenuButton>
        <Button
          shape="circle"
          onClick={() => setIsDrawerOpen(true)}
          overrides={{
            BaseButton: {
              style: ({ $theme }) => ({
                boxShadow: $theme.lighting.shadow600,
              }),
            },
          }}
        >
          <Menu size={24} />
        </Button>
      </FloatingMenuButton>

      <Drawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        anchor={ANCHOR.right}
        size="280px"
      >
        <div className={css({ padding: '24px' })}>
          <NavContent />
        </div>
      </Drawer>
    </PageContainer>
  );
};

export default ManualPage;
